/**
 * R2 Storage Client
 * Frontend client for interacting with R2 storage via the Cloudflare Worker API
 */

import { supabase } from './supabase';
import { logger } from './logger';

// Get the API base URL from environment
const API_BASE = import.meta.env.VITE_AI_WORKER_URL || '';

export type StorageBucket = 'staging-images' | 'style-images';

/**
 * Get the current user's auth token
 */
async function getAuthToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Make an authenticated request to the storage API
 */
async function storageRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<{ data: T | null; error: string | null }> {
  const token = await getAuthToken();
  if (!token) {
    return { data: null, error: 'Not authenticated' };
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { data: null, error: (errorData as { error?: string }).error || `Request failed: ${response.status}` };
    }

    const data = await response.json() as T;
    return { data, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Request failed';
    logger.error('Storage request error:', err);
    return { data: null, error: message };
  }
}

/**
 * Upload a file to R2 storage
 */
export async function uploadToR2(
  bucket: StorageBucket,
  path: string,
  file: File | Blob,
  contentType?: string
): Promise<{ success: boolean; error?: string }> {
  const token = await getAuthToken();
  if (!token) {
    return { success: false, error: 'Not authenticated' };
  }

  try {
    // Use multipart form data for file upload
    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucket', bucket);
    formData.append('path', path);

    const response = await fetch(`${API_BASE}/storage/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: (errorData as { error?: string }).error || 'Upload failed' };
    }

    return { success: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Upload failed';
    logger.error('R2 upload error:', err);
    return { success: false, error: message };
  }
}

/**
 * Upload base64 data to R2 storage
 */
export async function uploadBase64ToR2(
  bucket: StorageBucket,
  path: string,
  base64Data: string,
  contentType: string
): Promise<{ success: boolean; error?: string }> {
  const result = await storageRequest<{ success: boolean }>('/storage/upload', {
    method: 'POST',
    body: JSON.stringify({
      bucket,
      path,
      data: base64Data,
      contentType,
    }),
  });

  if (result.error) {
    return { success: false, error: result.error };
  }

  return { success: true };
}

/**
 * Get a signed URL for a file in R2 storage
 */
export async function getR2SignedUrl(
  bucket: StorageBucket,
  path: string,
  expiresIn?: number
): Promise<string | null> {
  const result = await storageRequest<{ signedUrl: string }>('/storage/signed-url', {
    method: 'POST',
    body: JSON.stringify({
      bucket,
      path,
      expiresIn,
    }),
  });

  if (result.error || !result.data) {
    logger.error('Failed to get signed URL:', result.error);
    return null;
  }

  return result.data.signedUrl;
}

/**
 * Get multiple signed URLs for files in R2 storage
 */
export async function getR2SignedUrls(
  bucket: StorageBucket,
  paths: string[]
): Promise<Map<string, string>> {
  const urlMap = new Map<string, string>();

  if (paths.length === 0) {
    return urlMap;
  }

  const result = await storageRequest<{ urls: Record<string, string> }>('/storage/signed-urls', {
    method: 'POST',
    body: JSON.stringify({
      bucket,
      paths,
    }),
  });

  if (result.error || !result.data) {
    logger.error('Failed to get signed URLs:', result.error);
    return urlMap;
  }

  for (const [path, url] of Object.entries(result.data.urls)) {
    urlMap.set(path, url);
  }

  return urlMap;
}

/**
 * Delete a file from R2 storage
 * Uses POST instead of DELETE to avoid body stripping issues with some proxies
 */
export async function deleteFromR2(
  bucket: StorageBucket,
  path: string
): Promise<{ success: boolean; error?: string }> {
  const result = await storageRequest<{ success: boolean }>('/storage/delete', {
    method: 'POST',
    body: JSON.stringify({
      bucket,
      path,
    }),
  });

  if (result.error) {
    return { success: false, error: result.error };
  }

  return { success: true };
}

/**
 * List files in R2 storage with a prefix
 */
export async function listR2Files(
  bucket: StorageBucket,
  prefix?: string,
  limit?: number
): Promise<{
  files: Array<{ key: string; size: number; uploaded: string }>;
  truncated: boolean;
  cursor?: string;
} | null> {
  const result = await storageRequest<{
    files: Array<{ key: string; size: number; uploaded: string }>;
    truncated: boolean;
    cursor?: string;
  }>('/storage/list', {
    method: 'POST',
    body: JSON.stringify({
      bucket,
      prefix,
      limit,
    }),
  });

  if (result.error || !result.data) {
    logger.error('Failed to list files:', result.error);
    return null;
  }

  return result.data;
}

/**
 * Check if a URL is an R2 signed URL (from our worker)
 */
export function isR2Url(url: string): boolean {
  if (!url) return false;
  return url.includes('/storage/serve?');
}

/**
 * Check if a URL is a Supabase storage URL
 */
export function isSupabaseStorageUrl(url: string): boolean {
  if (!url) return false;
  return url.includes('/storage/v1/object/');
}

/**
 * Extract the file path from an R2 signed URL
 */
export function extractR2Path(url: string): string | null {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('path');
  } catch {
    return null;
  }
}

/**
 * Generate a unique file path for storage (legacy - use generateOriginalImagePath or generateStagedImagePath)
 */
export function generateStoragePath(
  userId: string,
  projectId: string,
  prefix: string = '',
  extension: string = 'jpg'
): string {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 9);
  const prefixPart = prefix ? `${prefix}-` : '';
  return `${userId}/${projectId}/${prefixPart}${timestamp}-${randomId}.${extension}`;
}

/**
 * Sanitize a filename for safe storage
 * Removes special characters, limits length, preserves extension
 */
function sanitizeFilename(filename: string): string {
  // Extract extension
  const lastDot = filename.lastIndexOf('.');
  const name = lastDot > 0 ? filename.substring(0, lastDot) : filename;
  const ext = lastDot > 0 ? filename.substring(lastDot + 1).toLowerCase() : '';

  // Sanitize name: lowercase, replace special chars with hyphens, limit length
  const sanitized = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50);

  return ext ? `${sanitized}.${ext}` : sanitized;
}

/**
 * Generate path for original image uploads
 * Path format: {userId}/{projectId}/original/{sanitizedFilename}-{timestamp}-{randomId}.{ext}
 */
export function generateOriginalImagePath(
  userId: string,
  projectId: string,
  filename: string
): string {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 9);
  const sanitized = sanitizeFilename(filename);

  // Extract extension from sanitized filename
  const lastDot = sanitized.lastIndexOf('.');
  const name = lastDot > 0 ? sanitized.substring(0, lastDot) : sanitized;
  const ext = lastDot > 0 ? sanitized.substring(lastDot + 1) : 'jpg';

  return `${userId}/${projectId}/original/${name}-${timestamp}-${randomId}.${ext}`;
}

/**
 * Generate path for processed/staged images
 * Path format: {userId}/{projectId}/processed/{stagedImageId}.{ext}
 */
export function generateStagedImagePath(
  userId: string,
  projectId: string,
  stagedImageId: string,
  extension: string = 'jpg'
): string {
  return `${userId}/${projectId}/processed/${stagedImageId}.${extension}`;
}
